import {noop} from '@github-ui/noop'
import {CheckIcon} from '@primer/octicons-react'
import {Box, IconButton, Text} from '@primer/react'
import {useCallback, useEffect, useState} from 'react'

type FieldWrapperProps = {
  name: string
  placeholder: string | React.ReactNode
  value?: React.ReactNode
  input?: React.ReactNode
  inputRef?: React.RefObject<HTMLInputElement>
  anchorProps?: React.HTMLAttributes<HTMLElement>
  canUpdate?: boolean
  isStatusField?: boolean
}

type MetadataButtonProps = {
  disabled: boolean
  children: React.ReactNode
  onClick: React.MouseEventHandler<HTMLButtonElement>
  anchorProps?: React.HTMLAttributes<HTMLButtonElement>
}

export function MetadataButton({children, onClick, anchorProps, disabled}: MetadataButtonProps) {
  const sx = disabled
    ? {}
    : {
        '&:hover': {
          backgroundColor: 'actionListItem.default.hoverBg',
        },
      }

  return (
    <Box
      as={disabled ? undefined : 'button'}
      onClick={disabled ? noop : onClick}
      sx={{
        border: 'none',
        borderRadius: 2,
        backgroundColor: 'transparent',
        textAlign: 'left',
        height: 'auto',
        flexGrow: 1,
        py: 1,
        display: 'flex',
        flexDirection: 'row',
        gap: 1,
        alignItems: 'flex-start',
        color: 'fg.muted',
        overflow: 'hidden',
        ...sx,
      }}
      {...anchorProps}
    >
      <Box sx={{fontSize: 0, wordBreak: 'break-word', overflow: 'hidden', textOverflow: 'ellipsis'}}>{children}</Box>
    </Box>
  )
}

export function FieldWrapper({
  name,
  placeholder,
  value,
  input,
  inputRef,
  anchorProps,
  canUpdate = true,
  isStatusField = false,
}: FieldWrapperProps) {
  const [showInput, setShowInput] = useState(false)

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      if (!anchorProps) {
        setShowInput(true)
      } else if (anchorProps?.onClick) {
        anchorProps.onClick(e)
      }
    },
    [anchorProps],
  )

  useEffect(() => {
    if (showInput === true && inputRef && inputRef.current) {
      inputRef.current.focus()
    }
  }, [inputRef, showInput])

  return (
    <Box as="li" sx={{mt: 0, listStyleType: 'none', display: 'flex', alignItems: 'center', gap: 2}}>
      <Text
        as="p"
        sx={{
          fontWeight: 500,
          fontSize: 0,
          color: 'fg.muted',
          marginBottom: 0,
          wordBreak: 'break-word',
          flexBasis: isStatusField ? 'auto' : '40%',
          flexShrink: 0,
          mt: 0,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {name}
      </Text>
      {showInput ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: 1,
            mb: 1,
          }}
        >
          {input}
          <IconButton
            sx={{flexShrink: 0}}
            aria-label="Cancel"
            size="small"
            icon={CheckIcon}
            onClick={() => setShowInput(false)}
            disabled={!canUpdate}
          />
        </Box>
      ) : (
        <MetadataButton anchorProps={anchorProps} onClick={handleClick} disabled={!canUpdate}>
          {value ? value : <Text sx={{color: 'fg.muted', fontSize: 0}}>{placeholder}</Text>}
        </MetadataButton>
      )}
    </Box>
  )
}

try{ MetadataButton.displayName ||= 'MetadataButton' } catch {}
try{ FieldWrapper.displayName ||= 'FieldWrapper' } catch {}